import { logger } from "@/utils/logger";

const REDIRECT_STORAGE_KEY = "auth_redirect_url";

/**
 * Redirect URL management using sessionStorage
 * Provides secure storage and validation for post-login redirects
 */
export const redirectManager: {
  storeRedirect: (url: string) => void;
  getRedirect: () => string | null;
  getRedirectSilent: () => string | null;
  clearRedirect: () => void;
} = {
  /**
   * Store a redirect URL in sessionStorage
   * Validates the URL to prevent open redirect attacks
   */
  storeRedirect: (url: string): void => {
    if (typeof window === "undefined") {
      return;
    }

    try {
      // Validate URL to prevent open redirect attacks
      const urlObj = new URL(url, window.location.origin);

      // Only allow same-origin redirects
      if (urlObj.origin !== window.location.origin) {
        logger.warn(
          "Invalid redirect URL origin - potential open redirect attack",
          {
            component: "RedirectManager",
            metadata: {
              redirectUrl: url,
              expectedOrigin: window.location.origin,
              actualOrigin: urlObj.origin,
            },
          }
        );
        return;
      }

      // Don't store if already on login page
      if (urlObj.pathname === "/login") {
        return;
      }

      sessionStorage.setItem(REDIRECT_STORAGE_KEY, url);
      logger.debug("Stored redirect URL", {
        component: "RedirectManager",
        metadata: { url },
      });
    } catch {
      logger.warn("Failed to store redirect URL", {
        component: "RedirectManager",
        metadata: { url },
      });
    }
  },

  /**
   * Get the stored redirect URL from sessionStorage
   * Logs only on first retrieval when value exists (to reduce excessive logging)
   */
  getRedirect: (): string | null => {
    if (typeof window === "undefined") {
      return null;
    }

    try {
      const redirect = sessionStorage.getItem(REDIRECT_STORAGE_KEY);
      // Only log when value exists (first retrieval) to reduce excessive logging
      if (redirect) {
        logger.debug("Retrieved redirect URL", {
          component: "RedirectManager",
          metadata: { url: redirect },
        });
      }
      return redirect;
    } catch {
      logger.warn("Failed to get redirect URL", {
        component: "RedirectManager",
      });
      return null;
    }
  },

  /**
   * Get the stored redirect URL from sessionStorage without logging
   * Use this method in render paths to avoid excessive logging
   */
  getRedirectSilent: (): string | null => {
    if (typeof window === "undefined") {
      return null;
    }

    try {
      return sessionStorage.getItem(REDIRECT_STORAGE_KEY);
    } catch {
      // Only log errors, not successful retrievals
      logger.warn("Failed to get redirect URL", {
        component: "RedirectManager",
      });
      return null;
    }
  },

  /**
   * Clear the stored redirect URL from sessionStorage
   */
  clearRedirect: (): void => {
    if (typeof window === "undefined") {
      return;
    }

    try {
      sessionStorage.removeItem(REDIRECT_STORAGE_KEY);
      logger.debug("Cleared redirect URL", {
        component: "RedirectManager",
      });
    } catch {
      logger.warn("Failed to clear redirect URL", {
        component: "RedirectManager",
      });
    }
  },
};
